import { useLazyLoadQuery } from 'react-relay';
import GET_POSTS_QUERY, {
  type GetPostsQuery,
  type GetPostsQuery$variables,
} from '../../graphql/__generated__/GetPostsQuery.graphql';
import type { NormalizedPost } from 'typings/posts';

const useGetPosts = (params: GetPostsQuery$variables = {}) => {
  const postsData = useLazyLoadQuery<GetPostsQuery>(GET_POSTS_QUERY, params, { fetchPolicy: 'store-or-network' });

  const results = postsData?.unified?.ai?.getPosts;

  // Check if we have edges (successful response)
  if (results?.edges) {
    const normalizedPosts = results.edges
      .filter((edge): edge is NonNullable<typeof edge> => edge !== null && edge !== undefined && edge.node !== null)
      .map((edge) => normalizePost(edge.node));

    return {
      results,
      normalizedPosts,
      pageInfo: results.pageInfo,
      totalCount: results.totalCount,
    };
  }

  return {
    results: null,
    normalizedPosts: [],
    pageInfo: null,
    totalCount: 0,
  };
};

// Helper function to format time ago
const formatTimeAgo = (dateString: string): string => {
  const now = new Date();
  const date = new Date(dateString);
  const diffInMs = now.getTime() - date.getTime();
  const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
  const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

  if (diffInMinutes < 60) {
    return `${diffInMinutes}m ago`;
  } else if (diffInHours < 24) {
    return `${diffInHours}h ago`;
  } else {
    return `${diffInDays}d ago`;
  }
};

// Helper function to get icon for category
const getCategoryIcon = (categoryName: string): string => {
  const iconMap: Record<string, string> = {
    // Atlassian Products
    Jira: '📋',
    'Advanced planning': '🗺️',
    'Jira Mobile Apps': '📱',
    'Jira Service Management': '🛠️',
    'Jira Product Discovery': '🔍',
    'Jira Align': '🎯',
    Confluence: '📚',
    Trello: '📋',
    Assist: '🤖',
    Opsgenie: '🚨',
    Statuspage: '📊',
    Bitbucket: '🐙',
    Pipelines: '⚡',
    Bamboo: '🎋',
    'Fisheye/Crucible': '👁️',
    Sourcetree: '🌳',
    'Atlassian Home': '🏠',
    'Atlassian Guard': '🛡️',
    'Atlassian Account': '👤',
    'Atlassian Automation': '⚙️',
    'Atlassian Platform': '🔧',
    'Atlassian Analytics': '📈',

    // Community Categories
    'Community Announcements': '📢',
    'Community Events': '🎉',
    Focus: '🎯',
    Talent: '💼',
    'Featured Groups': '⭐',
    'Community Groups': '👥',
    'Product Groups': '🏷️',
    'Product Feedback Groups': '💬',
    'Industry Groups': '🏢',
  };

  return iconMap[categoryName] || '🧩'; // Default fallback icon
};

// Normalization function to convert GraphQL post to NormalizedPost
const normalizePost = (post: any): NormalizedPost => {
  return {
    id: post.id,
    title: post.title,
    content: post.content,
    author: post.author?.nickname || 'Unknown Author',
    timeAgo: formatTimeAgo(post.created_at),
    views: 0, // Not available in current GraphQL response
    comments: post.responses?.length || 0,
    likes: post.like_ids?.length || 0,
    productTag: post.category
      ? {
          name: post.category.name,
          icon: getCategoryIcon(post.category.name),
          href: post.category.href || `/category/${post.category.id}`,
        }
      : undefined,
    articleType: post.type as 'QUESTION' | 'ARTICLE' | 'DISCUSSION',
    tags: post.tags || [],
    isPinned: post.is_pinned,
    isLocked: post.is_locked,
    createdAt: post.created_at,
    updatedAt: post.updated_at,
  };
};

export default useGetPosts;
